// src/components/WatchPartyRail.jsx
import React, { memo } from "react";
import { Play, Clock } from "lucide-react";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

/**
 * WatchPartyRail
 * Update:
 * - Offsets the stories row slightly to the RIGHT under the header
 * to create visual contrast + less rigid alignment.
 * - Keeps header anchored; only the clickable items shift.
 *
 * "Alive" pass (LIVE pill only):
 * - LIVE pill gets a subtle breathing glow + tiny pulse dot inside the pill
 * - SOON stays static
 */
function WatchPartyRail({
  title = "Live Watch Parties",
  items = [],
  onJoin,
  className = "",
}) {
  return (
    <div className={cx("mt-0", className)}>
      {/* Header: small title row (top) */}
      <div className="flex items-center gap-2">
        <Play className="h-4 w-4 text-cyan-300/85" />
        <p className="text-[12px] tracking-[0.26em] uppercase text-white/70">
          {title}
        </p>
      </div>

      {/* Stories row: shifted slightly right */}
      <div className="mt-3">
        <div className={cx("pl-5 sm:pl-6", "pr-1")}>
          <div
            className={cx(
              "flex items-start justify-start gap-4",
              "overflow-x-auto scrollbar-hide pb-2",
              "select-none"
            )}
            style={{ caretColor: "transparent" }}
          >
            {items.length === 0 ? (
              <div className="text-sm text-white/55 py-2">
                No live watch parties right now.
              </div>
            ) : (
              items.slice(0, 6).map((wp) => {
                const status = String(wp.status || "live").toLowerCase();
                const isWaiting = status === "waiting";
                const isLive = !isWaiting;

                return (
                  <button
                    key={wp.id}
                    type="button"
                    onClick={() => onJoin?.(wp)}
                    className={cx(
                      "group shrink-0 text-center select-none",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/25 rounded-2xl"
                    )}
                    style={{ caretColor: "transparent" }}
                    title={wp.title || "Watch Party"}
                  >
                    {/* Circle cover */}
                    <div className="relative mx-auto">
                      <div
                        className={cx(
                          "h-[56px] w-[56px] rounded-full p-[2px]",
                          "bg-gradient-to-b",
                          isWaiting
                            ? "from-white/10 via-white/10 to-white/5"
                            : "from-cyan-300/55 via-white/10 to-white/5"
                        )}
                      >
                        <div className="relative h-full w-full rounded-full overflow-hidden border border-white/10 bg-white/[0.04] backdrop-blur-md">
                          {wp.cover ? (
                            <img
                              src={wp.cover}
                              alt=""
                              className="h-full w-full object-cover opacity-90 group-hover:opacity-100 transition"
                              loading="lazy"
                              draggable={false}
                            />
                          ) : (
                            <div className="h-full w-full grid place-items-center">
                              <div
                                className={cx(
                                  "h-7 w-7 rounded-xl border border-white/10",
                                  "bg-black/25 backdrop-blur-md grid place-items-center"
                                )}
                              >
                                {isWaiting ? (
                                  <Clock className="h-4 w-4 text-white/70" />
                                ) : (
                                  <Play className="h-4 w-4 text-cyan-200/90" />
                                )}
                              </div>
                            </div>
                          )}

                          {/* soft vignette */}
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
                        </div>
                      </div>

                      {/* Status pill: tighter + centered (LIVE animated, SOON static) */}
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                        <span
                          className={cx(
                            "px-2 py-[3px] rounded-full border backdrop-blur-md",
                            "text-[9px] tracking-[0.18em] uppercase leading-none",
                            "inline-flex items-center gap-1.5",
                            isWaiting
                              ? "border-white/10 bg-white/[0.06] text-white/70"
                              : cx(
                                  "border-cyan-300/25 bg-cyan-300/10 text-cyan-200",
                                  "shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_0_16px_rgba(34,211,238,0.18)]",
                                  "animate-[livepill_1.6s_ease-in-out_infinite]"
                                )
                          )}
                        >
                          {!isWaiting && (
                            <span
                              aria-hidden
                              className={cx(
                                "relative inline-block h-[6px] w-[6px] rounded-full",
                                "bg-cyan-300/90",
                                "shadow-[0_0_10px_rgba(34,211,238,0.35)]"
                              )}
                            >
                              <span className="absolute inset-0 rounded-full bg-cyan-300/25 animate-ping" />
                            </span>
                          )}
                          {isWaiting ? "Soon" : "Live"}
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <div className="mt-3 w-[74px] mx-auto">
                      <p className="text-[10.5px] text-white/85 truncate">
                        {wp.title || "Watch Party"}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Local keyframes (no global CSS file needed) */}
      <style>{`
        @keyframes livepill {
          0%, 100% {
            transform: translateY(0);
            box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.08), 0 0 14px rgba(34, 211, 238, 0.12);
          }
          50% {
            transform: translateY(-0.5px);
            box-shadow: 0 0 0 1px rgba(34, 211, 238, 0.12), 0 0 20px rgba(34, 211, 238, 0.22);
          }
        }
      `}</style>
    </div>
  );
}

export default memo(WatchPartyRail);
